import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Notification } from '../types';
import {
  Bell,
  TrendingUp,
  Gift,
  Clock,
  CheckCircle,
  XCircle,
  UserMinus,
} from 'lucide-react';

interface NotificationItemProps {
  notification: Notification;
  onRead: (notificationId: number) => void;
}

/**
 * NotificationItem Component (Issue #496)
 *
 * Displays a single notification with icon, title, message, and timestamp.
 * Clicking the notification marks it as read and navigates to the associated link.
 */
export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onRead,
}) => {
  const navigate = useNavigate();

  // Get icon based on notification type
  const getIcon = () => {
    switch (notification.type) {
      case 'waitlist_join':
        return <Bell className="w-5 h-5 text-dusty-teal" />;
      case 'position_change':
        return <TrendingUp className="w-5 h-5 text-green-500" />;
      case 'slot_offer':
        return <Gift className="w-5 h-5 text-purple-500" />;
      case 'offer_expiring':
        return <Clock className="w-5 h-5 text-orange-500" />;
      case 'offer_accepted':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'offer_declined':
        return <XCircle className="w-5 h-5 text-gray-500" />;
      case 'removed_from_waitlist':
        return <UserMinus className="w-5 h-5 text-red-500" />;
      default:
        return <Bell className="w-5 h-5 text-gray-500" />;
    }
  };

  // Format timestamp to relative time
  const getRelativeTime = (timestamp: string): string => {
    const now = new Date();
    const notificationTime = new Date(timestamp);
    const diffMs = now.getTime() - notificationTime.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

    return notificationTime.toLocaleDateString();
  };

  // Handle notification click
  const handleClick = () => {
    // Mark as read if not already
    if (!notification.read) {
      onRead(notification.id);
    }

    // Navigate to link if present
    if (notification.link) {
      navigate(notification.link);
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`
        flex items-start gap-3 p-3 rounded-lg transition-colors cursor-pointer
        ${notification.read ? 'bg-white hover:bg-gray-50' : 'bg-moonbone hover:bg-moonbone'}
      `}
    >
      {/* Unread indicator */}
      {!notification.read && (
        <div className="flex-shrink-0 w-2 h-2 mt-2 bg-moonbone0 rounded-full" />
      )}

      {/* Icon */}
      <div className="flex-shrink-0 mt-0.5">{getIcon()}</div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${notification.read ? 'text-gray-900' : 'text-gray-900'}`}>
          {notification.title}
        </p>
        <p className="text-sm text-gray-600 mt-0.5">{notification.message}</p>
        <p className="text-xs text-gray-400 mt-1">
          {getRelativeTime(notification.created_at)}
        </p>
      </div>
    </div>
  );
};

export default NotificationItem;
